import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { HeaderPlaylist } from '@/components/atoms/HeaderPlaylist';
import { InfoTrack } from '@/components/atoms/InfoTrack';
import { ShareTrack } from '@/components/atoms/ShareTrack';
import { Spinner } from '@/components/atoms/Spinner';
import {
  getTrackBeezerBySearch,
  selectSearchResults
} from '@/features/search/searchSlice';
import {
  getSingleTRackByIdBeezer,
  getSingleTrackBySpotify,
  selectSingleTrack,
  setAutoPlay,
  setCurrentTime,
  setIsUsingCurrentTime,
  setTrackSingleId
} from '@/features/singleTrack/singleTrackSlice';
import { SelectAuth } from '@/features/spotifyAuth/authSlice';
import { ITracksId } from '@/interface';
import { testWebShare } from '@/utils/webShare';
import { DownOutlined } from '@ant-design/icons';
import clx from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  default as AudioPlayer,
  default as H5AudioPlayer,
  RHAP_UI
} from 'react-h5-audio-player';
import { useNavigate, useParams } from 'react-router-dom';
import './singleTrack.scss';

export const SingleTrack = () => {
  const { trackId, status, currentTime, isAutoPlay } =
    useAppSelector(selectSingleTrack);
  const { searchResults } = useAppSelector(selectSearchResults);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [trackValue, setTrackValue] = useState<ITracksId>();
  const { isGuest } = useAppSelector(SelectAuth);
  const music = useRef<H5AudioPlayer>(null);

  const backRouter = () => {
    setIsHidden(true);
    setCurrentAudioTime();
    dispatch(setTrackSingleId(trackValue as ITracksId));
    dispatch(setIsUsingCurrentTime(true));
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  const classMain = clx('o-single', {
    'o-single--hidden': isHidden
  });

  const handleClickPrevious = (): void => {
    if (currentIndex === 0) {
      setCurrentIndex(searchResults.items.length - 1);
      setTrackValue(searchResults.items[searchResults.items.length - 1]);
    } else {
      setCurrentIndex(currentIndex - 1);
      setTrackValue(searchResults.items[currentIndex - 1]?.track as ITracksId);
    }
    setCurrentAudioTime();
  };
  const handleClickNext = (): void => {
    if (currentIndex === searchResults.items.length - 1) {
      setCurrentIndex(0);
      setTrackValue(searchResults.items[0].track as ITracksId);
    } else {
      setCurrentIndex(currentIndex + 1);
      setTrackValue(searchResults.items[currentIndex + 1]?.track as ITracksId);
    }
    setCurrentAudioTime();
  };

  const setCurrentAudioTime = () => {
    if (!music.current?.audio?.current) return;
    dispatch(setCurrentTime(music.current.audio.current.currentTime));
  };

  const handlePause = () => {
    dispatch(setAutoPlay(false));
  };

  const handlePlay = () => {
    dispatch(setAutoPlay(true));
  };

  useEffect(() => {
    if (!params.id) return;
    if (isGuest) {
      dispatch(getSingleTRackByIdBeezer(params.id));
    } else {
      dispatch(getSingleTrackBySpotify(params.id));
    }
    return () => {
      setTrackValue(undefined);
    };
  }, [dispatch, isGuest, params.id]);

  const currentTrack = searchResults?.items.find(
    (item) => item.track?.artists[0]?.name === trackId?.track?.artists[0]?.name
  );

  useEffect(() => {
    if (trackId?.track?.id) {
      setTrackValue(trackId);
    }
    if (!trackValue) return;
    if (!searchResults?.items?.length) {
      dispatch(getTrackBeezerBySearch(trackValue?.track?.artists[0]?.name));
    }
    return () => {
      setTrackValue(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, trackId, searchResults?.items]);

  useEffect(() => {
    if (!trackId) return;
    if (!currentTrack) {
      dispatch(getTrackBeezerBySearch(trackId?.track?.artists[0]?.name));
    }
  }, [dispatch, currentTrack, trackId]);

  useLayoutEffect(() => {
    if (status !== 'ok') return;
    if (!music.current?.audio?.current) return;
    music.current.audio.current.pause();
    music.current.audio.current.currentTime = currentTime || 0;
    isAutoPlay && music.current.audio.current.play();

    return () => {
      setCurrentAudioTime();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, status]);

  useEffect(() => {
    if (!currentIndex) return;
    if (!music.current?.audio?.current) return;
    music.current.audio.current.currentTime = 0;
  }, [currentIndex]);

  if (status === 'loading') {
    return (
      <main className='o-singleTrack__loading'>
        <Spinner />
      </main>
    );
  }

  if (trackId && Object.keys(trackId).length > 0) {
    return (
      <main className={classMain}>
        <motion.main
          initial={{ opacity: 0, y: -400 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className='o-singleTrack'>
          <section className='o-singleTrack__down'>
            <DownOutlined onClick={() => backRouter()} />
            <ShareTrack
              onTouchStart={() =>
                testWebShare({
                  text:
                    trackValue?.track?.name +
                    ' ' +
                    window.location.origin +
                    '/single/' +
                    trackValue?.id,
                  files: [trackValue?.album?.cover_big ?? 'cover_big']
                })
              }
            />
          </section>
          <section className='o-singleTrack__image'>
            <HeaderPlaylist
              image={trackValue?.album?.cover_big ?? ''}
              name={''}
              navigation={undefined}
            />
            <InfoTrack
              className='o-singleTrack__info'
              isToAudio={true}
              album={trackValue?.track?.name ?? ''}
              artist={trackValue?.track?.artists[0].name}
            />
          </section>
          <section className='o-singleTrack__audio'>
            <AudioPlayer
              ref={music}
              className='o-singleTrack__player'
              autoPlay={isAutoPlay}
              src={trackValue?.track?.preview_url}
              showSkipControls={true}
              showJumpControls={false}
              onClickPrevious={() => handleClickPrevious()}
              onClickNext={() => handleClickNext()}
              onEnded={() => handleClickNext()}
              customAdditionalControls={[]}
              customControlsSection={[
                RHAP_UI.ADDITIONAL_CONTROLS,
                RHAP_UI.MAIN_CONTROLS,
                RHAP_UI.VOLUME_CONTROLS
              ]}
              onPause={() => handlePause()}
              onPlay={() => handlePlay()}
            />
          </section>
        </motion.main>
      </main>
    );
  } else {
    return null;
  }
};
